import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { LogOut, Lock, MessageSquare, Home, LayoutDashboard, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { enrollmentOpen, reenrollmentOpen } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!user) { setUnread(0); return undefined; }
    const load = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [user, location.pathname]);

  // Fermer le menu mobile lors d'un changement de route
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const dashboardLink = user?.role === "admin" ? "/admin" : user?.role === "parent" ? "/parent" : "/enseignant";
  const onDashboard = location.pathname.startsWith(dashboardLink);
  const onHome = location.pathname === "/";

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-[0_4px_30px_rgba(0,0,0,0.03)]"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <img src="/logo.png" alt="Mosquée des Alpes Grenoble" className="h-10 w-auto md:h-14" />
          <div className="leading-tight hidden sm:block">
            <div className="font-[Outfit] font-semibold text-base md:text-lg tracking-tight text-[#1A2547]">
              Mosquée des Alpes
            </div>
            <div className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-[#5c6374]">
              Teisseire · Grenoble
            </div>
          </div>
        </Link>

        {/* Nav desktop */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/" className={`hover:text-[#1E3A8A] transition-colors ${onHome ? "text-[#1E3A8A] font-semibold" : "text-[#5c6374]"}`} data-testid="nav-home">Accueil</Link>
          {user && (
            <Link to={dashboardLink} className={`hover:text-[#1E3A8A] transition-colors ${onDashboard ? "text-[#1E3A8A] font-semibold" : "text-[#5c6374]"}`} data-testid="nav-dashboard">
              Tableau de bord
            </Link>
          )}
          {user?.is_teacher && (
            <Link to="/enseignant" className={`hover:text-[#1E3A8A] transition-colors ${location.pathname.startsWith("/enseignant") ? "text-[#1E3A8A] font-semibold" : "text-[#5c6374]"}`} data-testid="nav-teacher-space">
              Espace enseignant
            </Link>
          )}
        </nav>

        {/* Côté droit desktop */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {unread > 0 && (
                <Link to={dashboardLink + "?tab=messages"} className="relative" data-testid="nav-unread">
                  <Button variant="ghost" size="sm" className="rounded-full">
                    <MessageSquare className="w-4 h-4 mr-1.5" />
                    Messages
                    <Badge className="ml-1.5 bg-[#C86444] text-white border-0 rounded-full px-1.5 py-0 text-[10px] h-4">{unread}</Badge>
                  </Button>
                </Link>
              )}
              <span className="text-sm text-[#5c6374]" data-testid="user-name">{user.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="rounded-full">
                <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
              </Button>
            </>
          ) : (
            <>
              {(enrollmentOpen || reenrollmentOpen) ? (
                <Link to="/" data-testid="cta-request-nav">
                  <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white">
                    {enrollmentOpen && reenrollmentOpen ? "Inscriptions" : enrollmentOpen ? "Formulaire de demande d'inscription" : "Réinscription"}
                  </Button>
                </Link>
              ) : (
                <Button size="sm" disabled className="rounded-full bg-[#E2E5EE] text-[#5c6374] cursor-not-allowed" data-testid="cta-request-nav-closed">
                  <Lock className="w-3.5 h-3.5 mr-1.5" /> Inscriptions fermées
                </Button>
              )}
              <Link to="/connexion" data-testid="login-link">
                <Button variant="ghost" size="sm" className="rounded-full">Connexion</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile : badge unread + bouton menu hamburger */}
        <div className="flex md:hidden items-center gap-1">
          {user && unread > 0 && (
            <Link to={dashboardLink + "?tab=messages"} className="relative" data-testid="nav-unread-mobile">
              <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full">
                <MessageSquare className="w-4 h-4" />
                <Badge className="absolute -top-0.5 -right-0.5 bg-[#C86444] text-white border-0 rounded-full px-1 py-0 text-[9px] h-4 min-w-4">{unread}</Badge>
              </Button>
            </Link>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuOpen((v) => !v)}
            className="h-11 w-11 p-0 rounded-full"
            data-testid="nav-mobile-toggle"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Drawer mobile */}
      {menuOpen && (
        <div className="md:hidden border-t border-[#EAE6DF] bg-white/95 backdrop-blur-xl" data-testid="nav-mobile-drawer">
          <div className="px-4 py-3 space-y-1">
            <MobileLink to="/" icon={Home} active={onHome} testid="m-nav-home">Accueil</MobileLink>
            {user && (
              <MobileLink to={dashboardLink} icon={LayoutDashboard} active={onDashboard} testid="m-nav-dashboard">
                Tableau de bord
              </MobileLink>
            )}
            {user?.is_teacher && (
              <MobileLink to="/enseignant" icon={LayoutDashboard} active={location.pathname.startsWith("/enseignant")} testid="m-nav-teacher-space">
                Espace enseignant
              </MobileLink>
            )}
            {user ? (
              <>
                <div className="px-3 py-2 text-xs text-[#8a92a3] border-t border-[#EAE6DF] mt-2 pt-3">
                  Connecté en tant que <span className="font-medium text-[#1A2547]">{user.name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-[#B0413E] hover:bg-[#FBE5E4]"
                  data-testid="m-logout"
                >
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </>
            ) : (
              <>
                {(enrollmentOpen || reenrollmentOpen) && (
                  <MobileLink to="/" testid="m-nav-cta">
                    <span className="text-[#1E3A8A] font-medium">
                      {enrollmentOpen && reenrollmentOpen ? "Inscriptions" : enrollmentOpen ? "Formulaire de demande d'inscription" : "Réinscription"}
                    </span>
                  </MobileLink>
                )}
                <MobileLink to="/connexion" testid="m-nav-login">Connexion</MobileLink>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, icon: Icon, active, children, testid }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${
        active ? "bg-[#E0E7FF] text-[#1E3A8A] font-semibold" : "text-[#1A2547] hover:bg-[#F4F1EB]"
      }`}
      data-testid={testid}
    >
      {Icon && <Icon className="w-4 h-4" strokeWidth={1.5} />}
      {children}
    </Link>
  );
}
